import React from 'react';
import { Table, Tag, Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck,
  faCircleXmark,
  faTriangleExclamation,
  faSpinner,
  faBan,
  faHourglassHalf,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { mark, chrome, status as statusHue } from '../../../analytics/vizTokens';
import { fmtNight, fmtUtc, fmtDuration, fmtCount, statusColor } from './format';

// Outcome is a state, so it always ships an icon and a label -- colour alone
// never carries it.
export const OUTCOMES = {
  PASS: { icon: faCircleCheck, color: statusHue.good, tag: 'success', label: 'dw_outcome_pass' },
  NOT_COUNTED: { icon: faCircleXmark, color: statusHue.critical, tag: 'error', label: 'dw_outcome_not_counted' },
  NO_VERDICT: { icon: faTriangleExclamation, color: statusHue.warning, tag: 'warning', label: 'dw_outcome_no_verdict' },
  NO_RUN: { icon: faBan, color: statusHue.critical, tag: 'error', label: 'dw_outcome_no_run' },
  RUNNING: { icon: faSpinner, color: '#0D9488', tag: 'processing', label: 'dw_outcome_running' },
  PENDING: { icon: faHourglassHalf, color: chrome.muted, tag: 'default', label: 'dw_outcome_pending' },
  // Since the legacy cutover a night ends in the check against the source, not a verdict.
  CHECKED: { icon: faCircleCheck, color: statusHue.good, tag: 'success', label: 'dw_outcome_checked' },
  CHECK_FAILED: { icon: faCircleXmark, color: statusHue.critical, tag: 'error', label: 'dw_outcome_check_failed' },
  NO_CHECK: { icon: faTriangleExclamation, color: statusHue.warning, tag: 'warning', label: 'dw_outcome_no_check' },
};

// One row per UTC calendar night -- including the nights nothing ran.
//
// That is the point of it. Every failure mode on this pipeline presents as
// "nothing happened": a trigger that skipped its first occurrence, a late-stage
// failure that wrote no verdict. A list of runs cannot show a night with no run
// in it, and the soak counter walks verdict rows, so it steps straight over one.
function NightHistory({ nights, cutOver }) {
  const { t } = useTranslation();
  const rows = nights ?? [];
  const showSnapshotAge = !cutOver || rows.some((n) => n.legacySnapshotAgeHours != null);
  const maxDuration = Math.max(0, ...rows.map((n) => n.durationSeconds ?? 0));

  // Oldest on the left so the strip reads like a timeline.
  const strip = [...rows].reverse();

  return (
    <div className="rounded-card border border-line bg-surface p-4 shadow-card">
      <div className="mb-1 text-sm font-semibold text-ink">{t('dw_nights_title')}</div>
      <p className="mb-4 max-w-3xl text-xs leading-relaxed text-muted">{t('dw_nights_desc')}</p>

      <div className="mb-4 overflow-x-auto">
        <div className="flex min-w-max gap-1.5">
          {strip.map((n) => {
            const o = OUTCOMES[n.outcome] ?? OUTCOMES.NO_RUN;
            return (
              <Tooltip
                key={n.nightUtc}
                title={(
                  <div className="text-xs">
                    <div className="font-semibold">{fmtNight(n.nightUtc)}: {t(o.label)}</div>
                    {n.runId != null && <div>{t('dw_run')} {n.runId} · {n.runStatus} · {fmtDuration(n.durationSeconds)}</div>}
                    {n.verdict && <div className="mt-1">{n.verdict}</div>}
                  </div>
                )}
              >
                <div className="flex w-14 cursor-help flex-col items-center gap-1 rounded-md border border-line py-2">
                  <FontAwesomeIcon icon={o.icon} style={{ color: o.color }} spin={n.outcome === 'RUNNING'} />
                  <span className="text-[10px] leading-tight text-muted">{fmtNight(n.nightUtc).slice(0, 3)}</span>
                  <span className="text-[11px] font-semibold leading-tight text-ink tabular-nums">
                    {fmtNight(n.nightUtc).slice(4)}
                  </span>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <Table
        size="small"
        rowKey="nightUtc"
        pagination={false}
        scroll={{ x: 'max-content' }}
        dataSource={rows}
        locale={{ emptyText: t('no_data') }}
        columns={[
          {
            title: t('dw_night_utc'),
            key: 'night',
            render: (n) => <span className="whitespace-nowrap text-sm font-semibold text-ink">{fmtNight(n.nightUtc)}</span>,
          },
          {
            title: t('dw_outcome'),
            key: 'outcome',
            render: (n) => {
              const o = OUTCOMES[n.outcome] ?? OUTCOMES.NO_RUN;
              return (
                <Tag color={o.tag} icon={<FontAwesomeIcon icon={o.icon} className="mr-1" spin={n.outcome === 'RUNNING'} />}>
                  {t(o.label)}
                </Tag>
              );
            },
          },
          {
            title: t('dw_run'),
            key: 'run',
            render: (n) => (n.runId == null ? <span className="text-muted">—</span> : (
              <span className="whitespace-nowrap tabular-nums">
                {n.runId} <Tag color={statusColor(n.runStatus)} className="ml-1">{n.runStatus}</Tag>
              </span>
            )),
          },
          {
            title: `${t('dw_started')} (UTC)`,
            key: 'started',
            render: (n) => <span className="whitespace-nowrap tabular-nums">{fmtUtc(n.runStartedAtUtc)}</span>,
          },
          {
            title: t('dw_duration'),
            key: 'duration',
            render: (n) => (
              <div className="flex min-w-40 items-center gap-2">
                <span className="relative block h-2 flex-1 rounded-sm" style={{ backgroundColor: chrome.grid }}>
                  {n.durationSeconds != null && maxDuration > 0 && (
                    <span
                      className="absolute inset-y-0 left-0 rounded-sm"
                      style={{ width: `${(n.durationSeconds / maxDuration) * 100}%`, backgroundColor: mark }}
                    />
                  )}
                </span>
                <span className="w-16 text-right text-xs tabular-nums text-ink">{fmtDuration(n.durationSeconds)}</span>
              </div>
            ),
          },
          {
            title: t('dw_rows_extracted'),
            key: 'rows',
            align: 'right',
            render: (n) => <span className="tabular-nums">{fmtCount(n.rowsExtracted)}</span>,
          },
          {
            title: t('dw_reconciled'),
            key: 'reconciled',
            align: 'center',
            render: (n) => (n.isReconciled == null ? <span className="text-muted">—</span> : (
              <Tag color={n.isReconciled ? 'success' : 'error'}>{n.isReconciled ? t('yes') : t('no')}</Tag>
            )),
          },
          {
            // The night's check: the source check since the cutover, the Tier 2 gate before it.
            // Gate-era nights stay labelled as such until they age out of the window.
            title: cutOver ? t('dw_source_check') : t('dw_gate'),
            key: 'gate',
            render: (n) => {
              if (!n.verdict) {
                if (n.sourceCheckStatus) {
                  return (
                    <span className={`whitespace-nowrap text-xs ${n.sourceCheckStatus === 'Succeeded' ? 'text-ink' : 'text-danger'}`}>
                      {n.sourceCheckStatus}
                    </span>
                  );
                }
                return n.errorStep
                  ? <span className="text-xs text-danger">{t('dw_failed_at')} {n.errorStep}</span>
                  : <span className="text-muted">—</span>;
              }
              return (
                <Tooltip title={n.verdict}>
                  <span className="cursor-help whitespace-nowrap text-xs text-ink">
                    {cutOver && `${t('dw_gate')}: `}
                    {n.verdict.split(' ')[0]} · {t('dw_unexplained_short', { count: n.unexplainedCount ?? 0 })}
                  </span>
                </Tooltip>
              );
            },
          },
          // Only means anything for a gate verdict, so it leaves once no night in view has one.
          ...(showSnapshotAge ? [{
            title: t('dw_snapshot_age'),
            key: 'snapshot',
            align: 'right',
            render: (n) => {
              if (n.legacySnapshotAgeHours == null) return <span className="text-muted">—</span>;
              // Over 26h the night is inconclusive whatever the verdict says.
              const stale = n.legacySnapshotAgeHours > 26;
              return (
                <span className={`tabular-nums ${stale ? 'font-semibold text-danger' : ''}`}>
                  {n.legacySnapshotAgeHours}h
                </span>
              );
            },
          }] : []),
        ]}
      />
    </div>
  );
}

export default NightHistory;
